import { useEffect, useRef, useState, useCallback } from "react";
import { Trash2 } from "lucide-react";

/**
 * SwipeableRow — Apple Mail style swipe-left-to-reveal-Delete with long-press
 * to enter multi-select mode.
 *
 * - Swipe left on the row reveals a red Delete button beneath.
 * - Tap red button → instant onDelete (no confirm sheet, matches the rest of the app).
 * - Tap the row anywhere else (when not swiped) bubbles up via the wrapped child.
 * - Long-press (~500ms) fires onLongPress.
 * - In multi-select mode, swipe and long-press are both disabled (parent handles taps).
 *
 * Pure pointer events — works for mouse and touch.
 */

const REVEAL_WIDTH = 88; // px — width of the red Delete tray
const LONG_PRESS_MS = 500;
const SWIPE_TRIGGER = 12; // px before we consider it a swipe and lock direction

type Props = {
  children: React.ReactNode;
  onDelete?: () => void;
  onLongPress?: () => void;
  /** Optional swipe-right action (e.g. mark done). When omitted, right-swipe is disabled. */
  onSwipeRight?: () => void;
  /** Optional swipe-left action. When omitted and onDelete is present, left-swipe reveals Delete. */
  onSwipeLeft?: () => void;
  /** Label hint shown in the swipe-right tray. */
  swipeRightLabel?: string;
  /** Label hint shown in the swipe-left tray. */
  swipeLeftLabel?: string;
  swipeRightClassName?: string;
  swipeRightStyle?: React.CSSProperties;
  swipeLeftClassName?: string;
  /** When true, swipe + long-press are disabled (e.g. multi-select mode). */
  disabled?: boolean;
};

export default function SwipeableRow({
  children,
  onDelete,
  onLongPress,
  onSwipeRight,
  onSwipeLeft,
  swipeRightLabel = "Update",
  swipeLeftLabel = onDelete ? "Delete" : "Skip",
  swipeRightClassName = "bg-emerald-600 text-white",
  swipeRightStyle,
  swipeLeftClassName,
  disabled = false,
}: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [tx, setTx] = useState(0); // current translateX, negative = revealed
  const [animating, setAnimating] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const startX = useRef(0);
  const startY = useRef(0);
  const startTx = useRef(0);
  const dragging = useRef(false);
  const direction = useRef<"none" | "horizontal" | "vertical">("none");
  const longPressTimer = useRef<number | null>(null);
  const longPressed = useRef(false);
  const suppressNextMouseClick = useRef(false);

  // Close swipe when disabled or unmounting.
  useEffect(() => {
    if (disabled) {
      setTx(0);
      setRevealed(false);
    }
  }, [disabled]);

  // Tap-outside to close revealed state.
  useEffect(() => {
    if (!revealed) return;
    function handlePointer(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setAnimating(true);
        setTx(0);
        setRevealed(false);
      }
    }
    window.addEventListener("pointerdown", handlePointer);
    return () => window.removeEventListener("pointerdown", handlePointer);
  }, [revealed]);

  const clearLongPress = useCallback(() => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  function onPointerDown(e: React.PointerEvent) {
    if (disabled) return;
    // Ignore right-clicks etc.
    if (e.button !== undefined && e.button !== 0) return;

    dragging.current = true;
    direction.current = "none";
    longPressed.current = false;
    startX.current = e.clientX;
    startY.current = e.clientY;
    startTx.current = tx;
    setAnimating(false);

    // Long-press only if currently closed and not yet swiped.
    if (!revealed && onLongPress) {
      clearLongPress();
      longPressTimer.current = window.setTimeout(() => {
        if (
          dragging.current &&
          direction.current === "none" &&
          Math.abs(tx) < 4
        ) {
          longPressed.current = true;
          onLongPress?.();
          // close swipe state
          setTx(0);
          setRevealed(false);
          dragging.current = false;
        }
      }, LONG_PRESS_MS);
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragging.current || disabled) return;
    const dx = e.clientX - startX.current;
    const dy = e.clientY - startY.current;

    // Decide direction once we move past the threshold.
    if (direction.current === "none") {
      if (Math.abs(dx) < SWIPE_TRIGGER && Math.abs(dy) < SWIPE_TRIGGER) return;
      // Vertical wins → cancel: let the page scroll.
      if (Math.abs(dy) > Math.abs(dx)) {
        direction.current = "vertical";
        dragging.current = false;
        clearLongPress();
        return;
      }
      direction.current = "horizontal";
      if (e.pointerType === "mouse") suppressNextMouseClick.current = true;
      clearLongPress();
      try {
        (e.target as Element).setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
    }

    if (direction.current !== "horizontal") return;

    let next = startTx.current + dx;
    // Clamp left action bounds.
    if (!onDelete && !onSwipeLeft && next < 0) next = 0;
    if (next < -REVEAL_WIDTH * 1.4) {
      const over = next + REVEAL_WIDTH * 1.4;
      next = -REVEAL_WIDTH * 1.4 + over * 0.25;
    }
    // Clamp right (done) bounds — only if swipe-right action wired up.
    if (!onSwipeRight && next > 0) next = 0;
    if (next > REVEAL_WIDTH * 1.4) {
      const over = next - REVEAL_WIDTH * 1.4;
      next = REVEAL_WIDTH * 1.4 + over * 0.25;
    }
    setTx(next);
  }

  function onPointerUp() {
    if (!dragging.current || disabled) {
      clearLongPress();
      dragging.current = false;
      return;
    }
    clearLongPress();
    dragging.current = false;
    if (direction.current !== "horizontal") return;

    // Snap to revealed or closed.
    setAnimating(true);
    if (tx <= -REVEAL_WIDTH * 0.55) {
      if (onSwipeLeft) {
        setTx(0);
        setRevealed(false);
        window.setTimeout(() => onSwipeLeft(), 60);
      } else if (onDelete) {
        setTx(-REVEAL_WIDTH);
        setRevealed(true);
      } else {
        setTx(0);
        setRevealed(false);
      }
    } else if (onSwipeRight && tx >= REVEAL_WIDTH * 0.55) {
      // Trigger the done action and bounce back — no persistent tray on the right.
      setTx(0);
      setRevealed(false);
      window.setTimeout(() => onSwipeRight(), 60);
    } else {
      setTx(0);
      setRevealed(false);
    }
  }

  // Intercept clicks after desktop drags / revealed / long-pressed so the row tap doesn't fire.
  function onClickCapture(e: React.MouseEvent) {
    if (suppressNextMouseClick.current) {
      e.preventDefault();
      e.stopPropagation();
      suppressNextMouseClick.current = false;
      longPressed.current = false;
      return;
    }

    if (revealed || longPressed.current) {
      e.preventDefault();
      e.stopPropagation();
      // Tapping anywhere on the row while revealed → close.
      if (revealed) {
        setAnimating(true);
        setTx(0);
        setRevealed(false);
      }
      longPressed.current = false;
    }
  }

  return (
    <div
      ref={rootRef}
      className="row-swipe relative"
      style={{ touchAction: "pan-y" }}
    >
      {/* Green "Done" tray on the LEFT (revealed by swiping right). */}
      {!disabled && onSwipeRight && (
        <div
          aria-hidden="true"
          className={`absolute inset-y-0 left-0 flex items-center justify-center rounded-l-xl pointer-events-none ${swipeRightClassName}`}
          style={{
            width: REVEAL_WIDTH,
            opacity: tx > 2 ? 1 : 0,
            transition: animating ? "opacity 180ms ease" : "none",
            ...swipeRightStyle,
          }}
        >
          <span className="inline-flex flex-col items-center gap-1 text-xs font-medium">
            <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M3.5 8.5l3 3 6-6.5" /></svg>
            {swipeRightLabel}
          </span>
        </div>
      )}

      {/* Left swipe tray. Delete is revealable; other left actions bounce closed. */}
      {!disabled && onSwipeLeft && (
        <div
          aria-hidden="true"
          className={`absolute inset-y-0 right-0 flex items-center justify-center rounded-r-xl pointer-events-none ${
            swipeLeftClassName ?? "bg-amber-600 text-white"
          }`}
          style={{
            width: REVEAL_WIDTH,
            opacity: tx < -2 ? 1 : 0,
            transition: animating ? "opacity 180ms ease" : "none",
          }}
        >
          <span className="inline-flex flex-col items-center gap-1 text-xs font-medium">
            {swipeLeftLabel}
          </span>
        </div>
      )}

      {!disabled && !onSwipeLeft && onDelete && (
        <button
          type="button"
          onClick={() => {
            // Close visually, then trigger delete.
            setAnimating(true);
            setTx(0);
            setRevealed(false);
            // Defer slightly so the close animation can start.
            window.setTimeout(() => onDelete(), 80);
          }}
          aria-label="Delete"
          className="absolute inset-y-0 right-0 flex items-center justify-center bg-destructive text-destructive-foreground rounded-r-xl"
          style={{
            width: REVEAL_WIDTH,
            // Hide the tray completely when closed so it doesn't show through rounded corners.
            opacity: tx < -2 ? 1 : 0,
            transition: animating ? "opacity 180ms ease" : "none",
          }}
          tabIndex={revealed ? 0 : -1}
        >
          <span className="inline-flex flex-col items-center gap-1 text-xs font-medium">
            <Trash2 className="h-4 w-4" />
            Delete
          </span>
        </button>
      )}

      {/* Foreground row */}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClickCapture={onClickCapture}
        style={{
          transform: `translate3d(${tx}px, 0, 0)`,
          transition: animating ? "transform 220ms cubic-bezier(0.32,0.72,0,1)" : "none",
        }}
        onTransitionEnd={() => setAnimating(false)}
      >
        {children}
      </div>
    </div>
  );
}
